import { resolveDateRange, type DateRangePreset } from './ui/DateRange.js';

export type FlowPeriod = DateRangePreset;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True for canonical UUID strings (excludes POS ids like `order_…`). */
export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

export function fmtNum(n: number) {
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return n.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

export function fmtDt(iso: string) {
  return (
    new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
}

export function getPeriodDates(
  period: FlowPeriod,
  customFrom: string,
  customTo: string,
): { from: string; to: string } {
  if (period === 'custom') return { from: customFrom, to: customTo };
  const r = resolveDateRange({ preset: period });
  const today = localDateKey(new Date());
  return { from: r.from ?? '1970-01-01', to: r.to ?? today };
}

export function periodToIsoRange(
  period: FlowPeriod,
  customFrom: string,
  customTo: string,
): { fromIso: string; toIso: string } | null {
  if (period === 'custom' && (!customFrom || !customTo)) return null;
  const { from, to } = getPeriodDates(period, customFrom, customTo);
  const fromIso = new Date(from).toISOString();
  const toEnd = new Date(to);
  toEnd.setHours(23, 59, 59, 999);
  return { fromIso, toIso: toEnd.toISOString() };
}

export function toDayKey(iso: string): string {
  return localDateKey(new Date(iso));
}

function localDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parse YYYY-MM-DD as local calendar date (avoids UTC shift from `new Date('YYYY-MM-DD')`). */
export function parseLocalDate(ymd: string): Date {
  const parts = ymd.split('-').map(Number);
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

export function isMultiDayRange(
  period: FlowPeriod,
  customFrom: string,
  customTo: string,
): boolean {
  const { from, to } = getPeriodDates(period, customFrom, customTo);
  return from !== to;
}

export function enumerateDays(from: string, to: string): { key: string; label: string }[] {
  const days: { key: string; label: string }[] = [];
  const cur = parseLocalDate(from);
  const end = parseLocalDate(to);
  cur.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  while (cur <= end) {
    const key = localDateKey(cur);
    days.push({
      key,
      label: cur.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
    });
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

/** Parse waste note into reason + detail (stored as "reason" or "reason — detail"). */
export function parseWasteNote(note: string | null): { reason: string; detail: string } {
  if (!note) return { reason: 'other', detail: '' };
  const sep = note.indexOf(' — ');
  if (sep === -1) return { reason: note, detail: '' };
  return { reason: note.slice(0, sep), detail: note.slice(sep + 3) };
}

export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
